<template>
	<div>
		<apeiron-permission-groups-update
			v-if="$store.state.tenantId === 'apeiron_uat'"
		/>

		<apeiron-permission-groups-update
			v-else-if="$store.state.tenantId === 'eMD_uat'"
		/>

		<apeiron-permission-groups-update
			v-else-if="$store.state.tenantId === 'eMD'"
		/>

		<div
			v-else
			class="xs-12 flex wrap xs-center"
		>
			<div class="xs-12 max6 mb10">
				<!-- Headline -->
				<div class="flex xs-between xs-middle xs-12 ta1 fs5 fw7 lh125 mt8 mb4">
					<div>{{ $t("update") }} {{ currentPermissionGroup.name }}</div>
					<div>
						<j-button type="tertiary" @click="$router.back()">{{ $t("back") }}</j-button>
					</div>
				</div>

				<!-- List of permissions -->
				<j-card class="xs-12 bg5 r2">
					<div v-for="permission in computedPermissions" :key="permission.id">
						<permissions-item
							:id="permission.id"
							:name="permission.name"
							:description="permission.description"
							:isSelected="permission.isSelected"
							:targetType="permission.targetType"
							@add="addPermission"
							@remove="removePermission"
						/>
					</div>

					<!-- Loading -->
					<div v-if="isLoading" class="p4 ta2">{{ $t("loading") }}</div>
				</j-card>
			</div>
		</div>
	</div>
</template>

<script>
import PermissionsItem from "../components/PermissionsItem"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import api from "@/modules/users/api/"

// mocked for apeiron
import ApeironPermissionGroupsUpdate from "../components/mockedApeiron/ApeironPermissionGroupsUpdate"

export default {
	components: {
		PermissionsItem,
		JButton,
		JCard,

		// mocked for apeiron
		ApeironPermissionGroupsUpdate
	},

	props: {
		/**
		 * Id of permissionGroup user wants to update
		 */
		id: {
			type: String,
			required: true
		}
	},

	data() {
		return {
			/**
			 * List of all available permissions
			 * Is fetched once in created and is cached here
			 */
			allPermissions: [],

			/**
			 * Permission group that users is currently updating
			 */

			currentPermissionGroup: {},

			isLoading: false
		}
	},

	computed: {
		computedPermissions() {
			// Go through list of all permissions
			// Find if particular permissions is assigned to currentPermissionGroup
			// If it is, append property isSelected=true so that we can show it's added
			// Also figure out what targetIds permission has so that we know if it's "all" or "" version
			return this.allPermissions.map(permission => {
				// Find if permission from allPermissions is in the currentPermissionGroup
				const found = this.currentPermissionGroup.targettedPermissions
					? this.currentPermissionGroup.targettedPermissions.find(
							item => item.permission.id === permission.id
					  )
					: null

				// Figure out what target type it is
				// If it's "all" or "" so that we know if to display "for creator" or "for all"
				const targetType =
					found && found.targetIds && found.targetIds.length
						? found.targetIds[0]
						: null

				// Return enhanced permission object
				// if permission exist in currentGroup, it will be _isSelected = true
				// if it doesn't exist, it will be _isSelected = false
				return {
					id: permission.id,
					name: permission.name,
					description: permission.description,
					isSelected: found ? true : false,
					targetType: targetType === "" ? "creator" : targetType
				}
			})
		}
	},

	methods: {
		async getAllPermissions() {
			this.isLoading = true
			const allPermissions = await api.readAllPermissions()
			this.isLoading = false

			if (allPermissions.errors) {
				this.$store.dispatch("showError", allPermissions.errors)
				this.allPermissions = []
				return
			}

			this.allPermissions = allPermissions.data.permissions
		},

		async getPermissionsOfGroup() {
			// Get all permissions groups and find the one user is updating based on id
			// Api doesn't support filtering so we have to fetch all permission groups
			this.isLoading = true
			const permissionGroups = await api.readAllPermissionGroups()
			this.isLoading = false

			if (permissionGroups.errors) {
				this.$store.dispatch("showError", permissionGroups.errors)
				return
			}

			// Find current permission group based on this.id
			const currentPermissionGroup = permissionGroups.data.permissionGroups.find(
				item => item.id === this.id
			)
			if (!currentPermissionGroup) return

			this.currentPermissionGroup = currentPermissionGroup
		},

		async addPermission({ targetType = "all", id }) {
			const res = await api.addPermissionToPermissionGroup({
				id: this.id, // id of permission group
				permissionId: id, // id of permission
				targetId: targetType === "all" ? "all" : "" // all vs ""
			})

			if (res.errors) this.$store.dispatch("showError", res.errors)

			// Refetch to update state
			this.getPermissionsOfGroup()
		},

		async removePermission({ targetType = "all", id }) {
			const res = await api.removePermissionFromPermissionGroup({
				id: this.id, // id of permission group
				permissionId: id, // id of permission
				targetId: targetType === "all" ? "all" : "" // all vs ""
			})

			if (res.errors) this.$store.dispatch("showError", res.errors)

			// Refetch to update state
			this.getPermissionsOfGroup()
		}
	},

	mounted() {
		this.getAllPermissions()
		this.getPermissionsOfGroup()
	}
}
</script>
